import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import Footer from '../sections/Footer';
import Menu from '../sections/Menu';
import PageMeta from '../seo/PageMeta';
import { buildBlogPostingStructuredData } from '../seo/structuredData';
import { getCategoryMetaByName, splitPostCategories } from '../blog/categories';
import { formatPostDate } from '../blog/dates';
import { getPostThumbnail } from '../blog/media';

const allowedEmails = [
  'iwona.bendig@egodziny.pl',
  'krzysztof.warda@egodziny.pl',
  'dominik.pogorzelski@egodziny.pl'
];

function SingleBlogPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    async function fetchPost() {
      const docRef = doc(db, 'blogs', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setPost(docSnap.data());
      }
    }
    fetchPost();
  }, [id]);

  const canonicalPath = `/blog/${id}`;
  const structuredData = useMemo(
    () =>
      post ? [buildBlogPostingStructuredData({ path: canonicalPath, post })] : [],
    [canonicalPath, post]
  );
  const shouldNoindex = post?.public === false;
  const postDescription =
    post?.meta_description ||
    post?.content?.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 180);
  const postCategories = splitPostCategories(post?.category);
  const postThumbnail = getPostThumbnail(post);
  const postDate = formatPostDate(post, '-');

  if (!post) return <p></p>;

  return (
    <>
      <PageMeta
        path="/blog/:id"
        fallbackTitle={post.title}
        fallbackDescription={postDescription}
        canonicalPath={canonicalPath}
        structuredData={structuredData}
        ogType="article"
        ogImage={postThumbnail || undefined}
        breadcrumbTitle={post.title}
        noindex={shouldNoindex}
      />
      <Menu />
      <div className='blog-page' style={{ padding: '2rem' }}>
        <div className='blog-top-section'>
          {postCategories.length > 0 ? (
            <div className="blog-post-category-badges">
              {postCategories.map((categoryName) => {
                const categoryMeta = getCategoryMetaByName(categoryName);
                return (
                  <span
                    key={`${id}-${categoryName}`}
                    className="blog-post-category-badge"
                    style={
                      categoryMeta
                        ? {
                            color: categoryMeta.color,
                            backgroundColor: categoryMeta.bgColor,
                            borderColor: categoryMeta.borderColor,
                          }
                        : undefined
                    }
                  >
                    {categoryMeta?.label || categoryName}
                  </span>
                );
              })}
            </div>
          ) : null}
          <h1 className="blog-post-title">{post.title}</h1>
          <div className="blog-post-meta">
            {post.author ? <span className="blog-post-meta-author">{post.author}</span> : null}
            {postDate ? <span className="blog-post-meta-item">{postDate}</span> : null}
            {post.reading_time ? <span className="blog-post-meta-item">{post.reading_time}</span> : null}
          </div>
          {postThumbnail ? (
            <img
              src={postThumbnail}
              alt={`Miniatura wpisu: ${post.title}`}
              className="blog-post-main-thumbnail"
            />
          ) : null}
          {post.public === false && (
              <p className="blog-post-private-warning">
                  Post jest prywatny. Użytkownicy nie mogą go zobaczyć.
              </p>
          )}

          {user?.email && allowedEmails.includes(user.email) && (
            <button className='button-default btn' onClick={() => navigate(`/edit/${id}`)}>Edytuj post</button>
          )}
        </div>

        <div className='blog-content' dangerouslySetInnerHTML={{ __html: post.content }} />
      </div>
      <Footer />
    </>
  );
}

export default SingleBlogPost;
